import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { CameraScanner } from '@/shared/components/scanner/CameraScanner';
import {
  ScanLine,
  Camera,
  Keyboard,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  Volume2,
  VolumeX
} from 'lucide-react';
import { toast } from 'sonner';
import { useMobile } from '../context/MobileContext';
import { trailsAPI } from '@/shared/services/api';

const MobileScanInput = ({
  title,
  subtitle,
  gateId,
  barcodeType,
  scanContext,
  expectedValue,
  onScanSuccess,
  onScanError,
  chainMapping,
  validateScan,
  autoAdvance = true,
  showHistory = true
}) => {
  const [scanMode, setScanMode] = useState('camera'); // 'camera' or 'manual'
  const [manualInput, setManualInput] = useState('');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const { addScan, isOnline, queuePendingScan } = useMobile();

  // Play success/error sound
  const playSound = useCallback((success) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = success ? 1800 : 300;
      oscillator.type = success ? 'sine' : 'square';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) { }
  }, [soundEnabled]);

  // Process a scanned barcode
  const processScan = async (barcode) => {
    if (!barcode || isProcessing) return;

    setIsProcessing(true);
    setLastScanResult(null);

    try {
      if (!isOnline) {
        const queuedResult = {
          success: true,
          result: 'QUEUED',
          message: 'Offline - queued for sync',
        };
        addScan({
          barcode,
          result: queuedResult.result,
          context: scanContext,
          gateId,
        });
        queuePendingScan({
          barcode,
          barcodeType,
          scanContext,
          gateId,
          chainMapping,
        });
        handleScanResult(barcode, queuedResult, { alreadyRecorded: true });
        return;
      }

      // If a custom validator is provided, use it
      if (validateScan) {
        const result = await validateScan(barcode);
        handleScanResult(barcode, result);
        return;
      }

      // Default validation via API
      try {
        const params = {
          gate_id: gateId,
          barcode_value: barcode,
          barcode_type: barcodeType,
          scan_context: scanContext
        };

        if (chainMapping) {
          Object.entries(chainMapping).forEach(([key, value]) => {
            if (value) params[`chain_${key}`] = value;
          });
        }

        const result = await trailsAPI.validateScan(params);

        handleScanResult(barcode, {
          success: result.passed,
          result: result.result,
          message: result.rejection_reason,
          scanId: result.scan_id
        });

      } catch (error) {
        console.error('Scan validation error:', error);
        handleScanResult(barcode, {
          success: false,
          result: 'ERROR',
          message: 'Failed to validate scan'
        });
      }
    } catch (error) {
      console.error('Outer process scan error:', error);
      setIsProcessing(false);
    }
  };

  const handleScanResult = (barcode, result, options = {}) => {
    setIsProcessing(false);
    setLastScanResult({
      barcode,
      ...result,
      timestamp: new Date().toISOString()
    });

    // Add to history
    if (!options.alreadyRecorded) {
      addScan({
        barcode,
        result: result.result,
        context: scanContext,
        gateId
      });
    }

    if (result.success) {
      playSound(true);
      toast.success('Scan Accepted', {
        description: barcode
      });

      if (autoAdvance) {
        onScanSuccess?.(barcode, result);
      }
    } else {
      playSound(false);
      toast.error('Scan Rejected', {
        description: result.message || 'Invalid barcode'
      });
      onScanError?.(barcode, result);
    }

    setManualInput('');
  };

  const handleCameraScan = (barcode) => {
    processScan(barcode);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      processScan(manualInput.trim());
    }
  };

  const resetScan = () => {
    setLastScanResult(null);
    setManualInput('');
  };

  return (
    <div className="space-y-4" data-testid="mobile-scan-input">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSoundEnabled(!soundEnabled)}
        >
          {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </Button>
      </div>

      {/* Expected Value Hint */}
      {expectedValue && (
        <Alert>
          <ScanLine className="h-4 w-4" />
          <AlertDescription>
            Expected: <span className="font-mono font-semibold">{expectedValue}</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={scanMode === 'camera' ? 'default' : 'outline'}
          className="flex-1 gap-2"
          onClick={() => setScanMode('camera')}
          data-testid="scan-mode-camera"
        >
          <Camera className="h-4 w-4" />
          Camera
        </Button>
        <Button
          variant={scanMode === 'manual' ? 'default' : 'outline'}
          className="flex-1 gap-2"
          onClick={() => setScanMode('manual')}
          data-testid="scan-mode-manual"
        >
          <Keyboard className="h-4 w-4" />
          Manual
        </Button>
      </div>

      {/* Scanner Area */}
      <Card>
        <CardContent className="p-4">
          {scanMode === 'camera' ? (
            <CameraScanner
              onScan={handleCameraScan}
              showControls={true}
              autoStart={false}
            />
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder="Enter barcode manually..."
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                className="text-lg font-mono"
                autoFocus
                data-testid="manual-barcode-input"
              />
              <Button
                type="submit"
                className="w-full gap-2"
                disabled={!manualInput.trim() || isProcessing}
                data-testid="manual-scan-submit"
              >
                <ScanLine className="h-4 w-4" />
                {isProcessing ? 'Processing...' : 'Submit Scan'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Last Scan Result */}
      {lastScanResult && (
        <Card className={`border-2 ${lastScanResult.success
          ? 'border-green-500 bg-green-50 dark:bg-green-950/20'
          : 'border-red-500 bg-red-50 dark:bg-red-950/20'
          }`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {lastScanResult.success ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  {lastScanResult.success ? 'Scan Accepted' : 'Scan Rejected'}
                </p>
                <p className="font-mono text-sm truncate">{lastScanResult.barcode}</p>
                {lastScanResult.message && (
                  <p className="text-sm text-muted-foreground mt-1">{lastScanResult.message}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={resetScan}>
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offline Warning */}
      {!isOnline && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You're offline. Scans will be queued and synced when connected.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default MobileScanInput;
